import { useState, useEffect } from "react";
import Modal from 'react-bootstrap/Modal';
import posthog from 'posthog-js';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Spinner from 'react-bootstrap/Spinner';
import Accordion from 'react-bootstrap/Accordion';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import httpService from "../../services/httpService";
import { useNavigate } from "react-router-dom";
import { useUiStore } from "../../store/store";

import {
    FaRegLightbulb, FaRegClock, FaRocketchat,
    FaLock, FaLockOpen, FaCheckCircle,
} from "react-icons/fa";

import "../../styles/ClubDetailsModal.scss";

const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;

const ClubDetailsModal = ({ show, onHide, clubGuid, inviteCode }) => {
    const [selectedClubDetails, setSelectedClubDetails] = useState(null);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [loadingClubDetails, setLoadingClubDetails] = useState(true);
    const [subscribing, setSubscribing] = useState(false);
    const navigate = useNavigate();
    const setListenerSubscribedToClub = useUiStore((s) => s.setListenerSubscribedToClub);

    const loadClubDetails = (guid) => {
        setLoadingClubDetails(true);
        httpService.get(`/dash/listener-podcast-club/${guid}`)
            .then((res) => {
                setSelectedClubDetails(res.data.podcast_club);
                setIsSubscribed(res.data.is_subscribed);
            })
            .catch(() => setSelectedClubDetails(null))
            .finally(() => setLoadingClubDetails(false));
    };

    useEffect(() => {
        if (show && clubGuid) {
            loadClubDetails(clubGuid);
        } else {
            setSelectedClubDetails(null);
        }
    }, [show, clubGuid]);

    const handleJoinClub = (guid) => {
        setSubscribing(true);
        const joinCall = inviteCode
            ? httpService.post('invite/join', { code: inviteCode })
            : httpService.post(`/dash/listener-podcast-club/${guid}/subscribe`);

        joinCall
            .then(() => {
                posthog.capture('listener_club_joined', {
                    club_guid: guid,
                    club_title: selectedClubDetails?.title ?? null,
                });
                setListenerSubscribedToClub(true);
                navigate('/listener-podcast-club/' + guid);
            })
            .catch((err) => console.error("Error joining club:", err))
            .finally(() => setSubscribing(false));
    };

    const club = selectedClubDetails;

    return (
        <Modal id="club-details" show={show} onHide={onHide} size="xl" scrollable>
            <Modal.Body className="p-0">

                {/* ── Loading ── */}
                {loadingClubDetails && (
                    <div className="text-center py-5">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading…</span>
                        </Spinner>
                    </div>
                )}

                {/* ── Error ── */}
                {!loadingClubDetails && !club && (
                    <div className="text-center p-5">
                        <p className="text-muted mb-3">Failed to load club details.</p>
                        <Button variant="outline-secondary" size="sm" onClick={onHide}>Close</Button>
                    </div>
                )}

                {/* ── Content ── */}
                {!loadingClubDetails && club && (
                    <>
                        {/* ── Hero ── */}
                        <div
                            className="cdm-hero"
                            style={{
                                backgroundImage: `url(${club.super_wide_image ?? '/img/club-defaults/super-wide.jpg'})`,
                            }}
                        >
                            <div className="cdm-hero__scrim">

                                {/* Close button top-right */}
                                <button className="cdm-hero__close" onClick={onHide} aria-label="Close">✕</button>

                                {/* Bottom: badges + title */}
                                <div>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        <Badge
                                            bg={club.is_public ? 'success' : 'secondary'}
                                            style={{ fontSize: '0.68rem' }}
                                        >
                                            {club.is_public ? <><FaLockOpen className="me-1" />Public</> : <><FaLock className="me-1" />Private</>}
                                        </Badge>
                                        {club.allow_discussions && (
                                            <Badge bg="info" style={{ fontSize: '0.68rem' }}>
                                                <FaRocketchat className="me-1" />Discussions
                                            </Badge>
                                        )}
                                        {isSubscribed && (
                                            <Badge bg="warning" text="dark" style={{ fontSize: '0.68rem' }}>
                                                <FaCheckCircle className="me-1" />Subscribed
                                            </Badge>
                                        )}
                                    </div>
                                    <h2 className="cdm-hero__title">{club.title}</h2>
                                </div>

                            </div>
                        </div>

                        {/* ── Body ── */}
                        <div className="cdm-body">

                            {/* Stats row + Join button */}
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
                                <div className="d-flex flex-wrap gap-2">
                                    {club.start_date_time && (
                                        <span className="cdm-stat">
                                            <FaRegClock />
                                            Starts {fmtDate(club.start_date_time)}
                                        </span>
                                    )}
                                    <span className={`cdm-stat${club.is_public ? ' cdm-stat--success' : ''}`}>
                                        {club.is_public ? <FaLockOpen /> : <FaLock />}
                                        {club.is_public ? 'Public' : 'Private'}
                                    </span>
                                    <span className={`cdm-stat${club.allow_discussions ? ' cdm-stat--success' : ' cdm-stat--warning'}`}>
                                        <FaRocketchat />
                                        Discussions {club.allow_discussions ? 'on' : 'off'}
                                    </span>
                                </div>

                                <Button
                                    variant="primary"
                                    disabled={subscribing || isSubscribed}
                                    onClick={() => handleJoinClub(club.guid)}
                                    style={{ minWidth: '140px' }}
                                >
                                    {subscribing
                                        ? 'Joining…'
                                        : isSubscribed
                                            ? <><FaCheckCircle className="me-1" />Subscribed</>
                                            : 'Join Club'
                                    }
                                </Button>
                            </div>

                            {/* Description */}
                            {club.description && (
                                <div
                                    className="cdm-desc mb-4 pb-4"
                                    style={{ borderBottom: '1px solid rgba(0,0,0,0.07)' }}
                                    dangerouslySetInnerHTML={{ __html: club.description }}
                                />
                            )}

                            {/* Tabs */}
                            <Tabs defaultActiveKey="skills" id="club-details-tabs" className="mb-3">

                                {/* ══ Skills tab ═══════════════════════ */}
                                <Tab
                                    eventKey="skills"
                                    title={<span><FaRegLightbulb className="me-1" />Skills to learn</span>}
                                >
                                    {club.skills && club.skills.length > 0 ? (
                                        <Row className="g-3 mt-1">
                                            {club.skills.map((skill) => (
                                                <Col key={skill.id} md={4} sm={6} className="d-flex">
                                                    <div className="cdm-skill-card w-100">
                                                        <div className="cdm-skill-card__icon">
                                                            <FaRegLightbulb />
                                                        </div>
                                                        <div className="cdm-skill-card__title">{skill.title}</div>
                                                        {skill.description && (
                                                            <p className="cdm-skill-card__desc">{skill.description}</p>
                                                        )}
                                                        <FaRegLightbulb className="cdm-skill-card__watermark" />
                                                    </div>
                                                </Col>
                                            ))}
                                        </Row>
                                    ) : (
                                        <p className="text-muted small mt-2">No skills listed for this club.</p>
                                    )}
                                </Tab>

                                {/* ══ Episodes tab ═════════════════════ */}
                                <Tab
                                    eventKey="episodes"
                                    title={<span><FaRegClock className="me-1" />Episodes</span>}
                                >
                                    {club.episodes && club.episodes.length > 0 ? (
                                        <Accordion className="mt-2">
                                            {club.episodes.map((episode) => {
                                                const img   = episode.pivot?.featured_image || episode.featured_image;
                                                const title = episode.pivot?.title || episode.title;
                                                const date  = episode.pivot?.start_date_time;
                                                const desc  = episode.pivot?.description || episode.description;
                                                const color = episode.pivot?.color || '#5A9CB5';

                                                return (
                                                    <Accordion.Item
                                                        key={episode.id}
                                                        eventKey={episode.id.toString()}
                                                        style={{ borderLeft: `5px solid ${color}` }}
                                                    >
                                                        <Accordion.Header>
                                                            <div className="d-flex align-items-center gap-3 w-100 pe-2">
                                                                <span className="fw-semibold" style={{ fontSize: '0.93rem' }}>{title}</span>
                                                                {date && (
                                                                    <span className="text-muted ms-auto" style={{ fontSize: '0.73rem', whiteSpace: 'nowrap' }}>
                                                                        <FaRegClock className="me-1" />
                                                                        {fmtDate(date)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </Accordion.Header>
                                                        <Accordion.Body>
                                                            <Row className="g-3">

                                                                {/* Thumbnail + date */}
                                                                {img && (
                                                                    <Col sm={3} className="text-center">
                                                                        <img
                                                                            src={img}
                                                                            alt={title}
                                                                            className="cdm-ep__img"
                                                                        />
                                                                        {date && (
                                                                            <div className="cdm-ep__date justify-content-center">
                                                                                <FaRegClock />
                                                                                {fmtDate(date)}
                                                                            </div>
                                                                        )}
                                                                    </Col>
                                                                )}

                                                                {/* Description + takeaways */}
                                                                <Col sm={img ? 9 : 12}>
                                                                    {desc && (
                                                                        <div
                                                                            className="cdm-ep__desc mb-3"
                                                                            dangerouslySetInnerHTML={{ __html: desc }}
                                                                        />
                                                                    )}

                                                                    {episode.key_takeaways && episode.key_takeaways.length > 0 && (
                                                                        <div>
                                                                            <div className="cdm-label">
                                                                                <FaRegLightbulb className="me-1" />
                                                                                Actionable Insights
                                                                            </div>
                                                                            {episode.key_takeaways.map((kt) => (
                                                                                <div key={kt.id} className="cdm-takeaway">
                                                                                    <div className="cdm-takeaway__title">{kt.title}</div>
                                                                                    {kt.insight && (
                                                                                        <p className="cdm-takeaway__text">
                                                                                            <strong>Insight:</strong> {kt.insight}
                                                                                        </p>
                                                                                    )}
                                                                                    {kt.implementation && (
                                                                                        <p className="cdm-takeaway__text">
                                                                                            <strong>Implementation:</strong> {kt.implementation}
                                                                                        </p>
                                                                                    )}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </Col>

                                                            </Row>
                                                        </Accordion.Body>
                                                    </Accordion.Item>
                                                );
                                            })}
                                        </Accordion>
                                    ) : (
                                        <p className="text-muted small mt-2">No episodes available for this club.</p>
                                    )}
                                </Tab>

                            </Tabs>
                        </div>
                    </>
                )}

            </Modal.Body>
        </Modal>
    );
};

export default ClubDetailsModal;
